<template>
  <div class="depositAccountCard">
    <div class="depositAccountCard__leftBorderStatus" :class="leftBorderStatusColor"></div>
    <div class="depositAccountCard__topRight">
      <div class="depositAccountCard__topRight__flexCol">
        <span>
          {{ logoText }}
        </span>
        <img :src="logo" />
      </div>
    </div>
    <div class="depositAccountCard__content">
      <div class="depositAccountCard__content__top">
        <span class="depositAccountCard__content__top__warning" :class="warningStatusColor"> ! </span>
        <h4 class="mt-2">
          {{ headerTitle }}
        </h4>
        <div class="depositAccountCard__content__top__verticalLine"></div>
        <span class="depositAccountCard__content__top__infoText">
          {{ headerText }}
        </span>
      </div>

      <hr class="mt-1" />

      <div class="depositAccountCard__content__grid mt-4">
        <div class="depositAccountCard__content__grid__left">
          <div v-for="(item, index) in items" :key="index" class="depositAccountCard__content__grid__left__item">
            <span>
              <i :class="item.icon" />
            </span>
            <div>
              <span>{{ item.title }}: </span>
              <strong :class="{ depositAccountCard__content__grid__left__item__letterSpacing: item.letterSpacing }">
                {{ item.value }}
              </strong>
            </div>
          </div>
          <hr />
        </div>

        <div class="depositAccountCard__content__grid__right">
          <hl-Button
            v-if="shouldShowDeleteButton"
            class="depositAccountCard__content__grid__right__buttonMobile"
            variant="danger"
            :disabled="isDeletingDeposit"
            :aria-busy="isDeletingDeposit"
            :aria-disabled="isDeletingDeposit"
            @click="$emit('delete')"
          >
            <div class="d-flex justify-content-center">
              <hl-LoaderDots v-if="isDeletingDeposit" variant="white" class="mr-2" />
              {{ deleteButtonText }}
            </div>
          </hl-Button>

          <hl-Button
            v-if="shouldShowSignButton"
            class="depositAccountCard__content__grid__right__buttonMobile"
            variant="primary"
            @click="$emit('sign')"
          >
            {{ signButtonText }}
          </hl-Button>

          <hl-Button
            v-if="shouldShowTerminationButton"
            class="depositAccountCard__content__grid__right__buttonMobile"
            variant="extra"
            @click="$emit('terminate')"
          >
            {{ terminateButtonText }}
          </hl-Button>

          <template v-if="$slots.contentText">
            <div class="depositAccountCard__content__grid__right__warning">
              <i class="budicon-alert-sign" />
            </div>

            <div>
              <slot name="contentText" />
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="shouldShowDeleteButton || shouldShowSignButton || shouldShowTerminationButton"
        class="depositAccountCard__content__buttons mt-4"
      >
        <hl-Button
          v-if="shouldShowDeleteButton"
          class="depositAccountCard__content__buttons__buttonDesktop"
          variant="danger"
          :disabled="isDeletingDeposit"
          :aria-busy="isDeletingDeposit"
          :aria-disabled="isDeletingDeposit"
          @click="$emit('delete')"
        >
          <div class="d-flex justify-content-center">
            <hl-LoaderDots v-if="isDeletingDeposit" variant="white" class="mr-2" />
            {{ deleteButtonText }}
          </div>
        </hl-Button>

        <hl-Button
          v-if="shouldShowSignButton"
          class="depositAccountCard__content__buttons__buttonDesktop"
          variant="primary"
          @click="$emit('sign')"
        >
          {{ signButtonText }}
        </hl-Button>

        <hl-Button
          v-if="shouldShowTerminationButton"
          class="depositAccountCard__content__buttons__buttonDesktop"
          variant="extra"
          @click="$emit('terminate')"
        >
          {{ terminateButtonText }}
        </hl-Button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots';

export default Vue.extend({
  name: 'DepositAccountCard',
  props: {
    status: {
      type: String,
      required: true,
    },
    headerTitle: {
      type: String,
      required: true,
    },
    headerText: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    logoText: {
      type: String,
      required: true,
    },
    shouldShowDeleteButton: {
      type: Boolean,
      required: true,
    },
    isDeletingDeposit: {
      type: Boolean,
      default: false,
    },
    shouldShowSignButton: {
      type: Boolean,
      required: true,
    },
    shouldShowTerminationButton: {
      type: Boolean,
      required: true,
    },
    deleteButtonText: {
      type: String,
      required: true,
    },
    signButtonText: {
      type: String,
      required: false,
    },
    terminateButtonText: {
      type: String,
      required: true,
    },
    logo: {
      type: String,
      required: true,
    },
  },
  components: {
    'hl-Button': Button,
    'hl-LoaderDots': LoaderDots,
  },
  computed: {
    warningStatusColor() {
      if (this.status === 'paid') {
        return 'depositAccountCard__content__top__warning__green';
      }
      if (this.status === 'ended') {
        return 'depositAccountCard__content__top__warning__red';
      }
      return 'depositAccountCard__content__top__warning__yellow';
    },
    leftBorderStatusColor() {
      if (this.status === 'paid') {
        return 'depositAccountCard__leftBorderStatus__backgroundGreen';
      }
      if (this.status === 'ended') {
        return 'depositAccountCard__leftBorderStatus__backgroundRed';
      }
      return 'depositAccountCard__leftBorderStatus__backgroundYellow';
    },
  },
});
</script>

<style lang="scss" scoped>
.depositAccountCard {
  position: relative;
  padding: 1rem;
  background: white;
  border: 1px solid $husleie-light-grey;
  border-radius: 12px;
}
.depositAccountCard__leftBorderStatus {
  position: absolute;
  width: 4px;
  height: 60px;
  left: 0;
  top: 2.43rem;
}
.depositAccountCard__leftBorderStatus__backgroundGreen {
  background: $primary;
}
.depositAccountCard__leftBorderStatus__backgroundYellow {
  background: #eed202;
}
.depositAccountCard__leftBorderStatus__backgroundRed {
  background: $husleie-red;
}
.depositAccountCard__topRight {
  display: none;
}
.depositAccountCard__content {
  padding: 0rem 1rem;
  margin-top: 2rem;
}
.depositAccountCard__content__top {
  display: flex;
  align-items: center;
  gap: 0rem 1rem;
}
.depositAccountCard__content__top__warning {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 9999px;
}
.depositAccountCard__content__top__warning__green {
  background: $primary;
  border: 0;
  font-size: 0;
}
.depositAccountCard__content__top__warning__yellow {
  background: #eed202;
  border: 1px solid black;
}
.depositAccountCard__content__top__warning__red {
  background: $husleie-red;
  border: 0;
  font-size: 0;
}
.depositAccountCard__content__top__verticalLine {
  display: none;
}
.depositAccountCard__content__top__infoText {
  display: none;
}
.depositAccountCard__content__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem 1rem;
}
.depositAccountCard__content__grid__left {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem 0rem;
}
.depositAccountCard__content__grid__left hr {
  display: relative;
  width: 100%;
}
.depositAccountCard__content__grid__left__item {
  display: flex;
  align-items: center;
  gap: 0rem 1rem;
}
.depositAccountCard__content__grid__left__item__letterSpacing {
  letter-spacing: 1.5px;
}
.depositAccountCard__content__grid__left__item i {
  font-size: 1.5rem;
}
.depositAccountCard__content__grid__right {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem 0rem;
}
.depositAccountCard__content__grid__right__warning {
  display: flex;
  justify-content: center;
  font-size: 1.5rem;
}
.depositAccountCard__content__grid__right__buttonMobile {
  width: 210px;
  border-radius: $border-radius-medium;
  padding: 0.7rem 1rem;
}
.depositAccountCard__content__buttons__buttonDesktop {
  display: none;
}

@media (min-width: 1038px) {
  .depositAccountCard__content__grid__right__buttonMobile {
    display: none;
  }
  .depositAccountCard {
    position: relative;
    padding: 1rem;
    background: white;
    border: 1px solid $husleie-light-grey;
  }
  .depositAccountCard__leftBorderStatus {
    position: absolute;
    width: 4px;
    height: 60px;
    left: 0;
    top: 3.12rem;
  }
  .depositAccountCard__topRight {
    display: flex;
    justify-content: flex-end;
  }
  .depositAccountCard__topRight__flexCol {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2rem 0rem;
  }
  .depositAccountCard__topRight__flexCol img {
    width: 100px;
  }
  .depositAccountCard__content {
    padding: 0rem 3rem 2rem 3rem;
    margin-top: 0;
  }
  .depositAccountCard__content hr {
    display: block;
    margin: 0 0 0 3rem;
    width: 70%;
  }
  .depositAccountCard__content__top {
    display: flex;
    align-items: center;
    gap: 0rem 1rem;
  }
  .depositAccountCard__content__top__warning {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    min-height: 30px;
    border-radius: 9999px;
  }
  .depositAccountCard__content__top__verticalLine {
    display: initial;
    height: 20px;
    border: 1px solid #f1f1f1;
  }
  .depositAccountCard__content__top__infoText {
    display: initial;
    margin-top: 0.2rem;
  }
  .depositAccountCard__content__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0rem 2rem;
  }
  .depositAccountCard__content__grid__left {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: max-content;
    gap: 0.8rem 0rem;
  }
  .depositAccountCard__content__grid__left hr {
    display: none;
  }
  .depositAccountCard__content__grid__right {
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: start;
    gap: 2rem 1rem;
  }
  .depositAccountCard__content__buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1rem 0;
  }
  .depositAccountCard__content__buttons__buttonDesktop {
    display: initial;
    border-radius: $border-radius-medium;
    width: 210px;
    padding: 0.7rem 1rem;
  }
}
</style>
